import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount or gift card code to the cart.
 *
 * Discount codes update the cart total immediately via Ajax Cart API.
 * Gift cards are stored on cart attributes and applied at checkout.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The discount error container.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartGiftCardError - The gift card error container.
 * @property {HTMLElement} cartGiftCardErrorIsDiscount - Error when a discount code is entered as gift card.
 * @property {HTMLElement} cartGiftCardErrorInvalid - Error when gift card validation fails.
 * @property {HTMLElement} cartGiftCardErrorShort - Error when gift card code is shorter than 8 characters.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartGiftCardError',
    'cartGiftCardErrorIsDiscount',
    'cartGiftCardErrorInvalid',
    'cartGiftCardErrorShort',
  ];

  /** Minimum Shopify gift card code length (legacy short codes need a 00 prefix). */
  static GIFT_CARD_CODE_MIN_LEN = 8;

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {string} code
   * @returns {string}
   */
  #normalizeCode(code) {
    return code.trim().replace(/[\s-]/g, '').toUpperCase();
  }

  /**
   * @param {object} body
   * @param {AbortSignal} signal
   */
  async #cartUpdate(body, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify(body),
    });

    const response = await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal,
    });

    return response.json();
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      // Gift cards live in attributes — redirect user to the gift-card field.
      const existingGiftCards = this.#existingGiftCards();
      if (existingGiftCards.includes(this.#normalizeCode(discountCodeValue))) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.#hideGiftCardErrors();

      const data = await this.#cartUpdate(
        {
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        },
        abortController.signal,
      );

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill[data-code-type="discount"]') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Validates gift card via gift-card-admin app proxy.
   * @param {string} code
   * @param {AbortSignal} signal
   * @returns {Promise<{ valid: boolean; reason?: string }>}
   */
  async #validateGiftCard(code, signal) {
    const url = `/apps/gift-cards/validate?code=${encodeURIComponent(code)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });

    let payload =
      /** @type {{ valid?: boolean; reason?: string; balance?: { amount?: string; currencyCode?: string } }} */ ({});
    try {
      payload = await response.json();
    } catch {
      payload = {};
    }

    return {
      valid: Boolean(payload.valid),
      reason: typeof payload.reason === 'string' ? payload.reason : undefined,
      balance: payload.balance,
    };
  }

  /**
   * Stores a gift card code on cart attributes (applied at checkout).
   * Rejects codes that are valid Shopify discount codes.
   * Validates against Shopify gift cards via app proxy.
   * @param {SubmitEvent} event
   */
  applyGiftCard = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const giftCardInput = form.querySelector('input[name="gift_card"]');
    if (!(giftCardInput instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const rawValue = giftCardInput.value;
    const giftCardValue = this.#normalizeCode(rawValue);
    if (!giftCardValue) return;

    this.#hideGiftCardErrors();
    this.#hideDiscountErrors();

    if (giftCardValue.length < CartDiscount.GIFT_CARD_CODE_MIN_LEN) {
      giftCardInput.value = '';
      this.#handleGiftCardError('short');
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const existingGiftCards = this.#existingGiftCards();
      if (existingGiftCards.includes(giftCardValue)) return;

      const existingDiscounts = this.#existingDiscounts();

      // Probe: if Shopify accepts it as a discount code, reject — wrong field.
      const probe = await this.#cartUpdate(
        {
          discount: [...existingDiscounts, rawValue.trim()].join(','),
        },
        abortController.signal,
      );

      const probeEntry = (probe.discount_codes || []).find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          this.#normalizeCode(discount.code) === giftCardValue || discount.code === rawValue.trim(),
      );

      // Restore discount codes without the probed value.
      await this.#cartUpdate(
        {
          discount: existingDiscounts.join(','),
        },
        abortController.signal,
      );

      if (probeEntry?.applicable === true) {
        giftCardInput.value = '';
        this.#handleGiftCardError('is_discount');
        return;
      }

      const validation = await this.#validateGiftCard(giftCardValue, abortController.signal);
      if (!validation.valid) {
        giftCardInput.value = '';
        this.#handleGiftCardError(validation.reason === 'invalid_format' ? 'short' : 'invalid');
        return;
      }

      const balanceCents = Math.round(Number(validation.balance?.amount || 0) * 100);
      if (!Number.isFinite(balanceCents) || balanceCents <= 0) {
        giftCardInput.value = '';
        this.#handleGiftCardError('invalid');
        return;
      }

      const nextGiftCards = [...existingGiftCards, giftCardValue];
      const nextBalances = [...this.#existingGiftCardBalances(), String(balanceCents)];
      const data = await this.#cartUpdate(
        {
          attributes: {
            gift_card_codes: nextGiftCards.join(','),
            gift_card_balances_cents: nextBalances.join(','),
          },
          sections: [this.dataset.sectionId],
        },
        abortController.signal,
      );

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill[data-code-type="discount"]');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const data = await this.#cartUpdate(
        {
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        },
        abortController.signal,
      );

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Removes a stored gift card code from cart attributes.
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeGiftCard = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const source = event.target.closest('[data-gift-card-code][data-code-type="gift_card"]');
    if (!(source instanceof HTMLElement)) return;

    const giftCardCode = source.dataset.giftCardCode;
    if (!giftCardCode) return;

    const existingGiftCards = this.#existingGiftCards();
    const index = existingGiftCards.indexOf(this.#normalizeCode(giftCardCode));
    if (index === -1) return;

    existingGiftCards.splice(index, 1);
    const existingBalances = this.#existingGiftCardBalances();
    if (index < existingBalances.length) {
      existingBalances.splice(index, 1);
    }

    const abortController = this.#createAbortController();

    try {
      const data = await this.#cartUpdate(
        {
          attributes: {
            gift_card_codes: existingGiftCards.join(','),
            gift_card_balances_cents: existingBalances.join(','),
          },
          sections: [this.dataset.sectionId],
        },
        abortController.signal,
      );

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * @param {'is_discount' | 'invalid' | 'short'} type
   */
  #handleGiftCardError(type) {
    const {
      cartGiftCardError,
      cartGiftCardErrorIsDiscount,
      cartGiftCardErrorInvalid,
      cartGiftCardErrorShort,
    } = this.refs;
    cartGiftCardError.classList.remove('hidden');
    if (type === 'is_discount') {
      cartGiftCardErrorIsDiscount.classList.remove('hidden');
    } else if (type === 'short') {
      cartGiftCardErrorShort.classList.remove('hidden');
    } else {
      cartGiftCardErrorInvalid.classList.remove('hidden');
    }
  }

  #hideDiscountErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }

  #hideGiftCardErrors() {
    const {
      cartGiftCardError,
      cartGiftCardErrorIsDiscount,
      cartGiftCardErrorInvalid,
      cartGiftCardErrorShort,
    } = this.refs;
    cartGiftCardError.classList.add('hidden');
    cartGiftCardErrorIsDiscount.classList.add('hidden');
    cartGiftCardErrorInvalid.classList.add('hidden');
    cartGiftCardErrorShort.classList.add('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill[data-code-type="discount"]');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Returns normalized gift card codes stored on the cart.
   * @returns {string[]}
   */
  #existingGiftCards() {
    /** @type {string[]} */
    const giftCards = [];
    const pills = this.querySelectorAll('.cart-discount__pill[data-code-type="gift_card"]');
    for (const pill of pills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.giftCardCode === 'string') {
        giftCards.push(this.#normalizeCode(pill.dataset.giftCardCode));
      }
    }

    return giftCards;
  }

  /**
   * Parallel balances (cents) for #existingGiftCards().
   * @returns {string[]}
   */
  #existingGiftCardBalances() {
    /** @type {string[]} */
    const balances = [];
    const pills = this.querySelectorAll('.cart-discount__pill[data-code-type="gift_card"]');
    for (const pill of pills) {
      if (!(pill instanceof HTMLLIElement)) continue;
      const raw = pill.dataset.giftCardBalanceCents || '0';
      balances.push(String(Number(raw) || 0));
    }
    return balances;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
